"use client"

import { useDebouncedValue } from "@/app/hooks/use-debounced-value"
import { ChevronIcon } from "@/app/icons/chevron"
import { SearchIcon } from "@/app/icons/search"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useMemo, useState } from "react"
import { NftFiltersMoleculeReatom } from "../store.reatom"
import { TraitValue } from "./trait-value"

interface Props {
  trait: {
    name: string
    values: {
      value: string
      count: number
    }[]
  }
}

export const Trait = reatomComponent<Props>(({ trait }) => {
  const { appliedTraitCounts, traitsSelection, setTraitsSelection } =
    useMolecule(NftFiltersMoleculeReatom)
  const [searchQuery, setSearchQuery] = useState("")

  const counts = appliedTraitCounts()
  const appliedValuesCount = counts[trait.name]

  const selection = traitsSelection()
  const traitSet = selection[trait.name]

  // Filter trait values based on search query
  const filteredValues = useMemo(() => {
    if (!searchQuery.trim()) return trait.values

    return trait.values.filter(({ value }) =>
      value.toLowerCase().includes(searchQuery.toLowerCase().trim()),
    )
  }, [trait.values, searchQuery])

  const debouncedFilteredValues = useDebouncedValue(filteredValues, 300)

  return (
    <Collapsible className="group/collapsible-trait flex flex-col gap-4">
      <CollapsibleTrigger asChild>
        <div className="flex cursor-pointer items-center justify-between gap-1">
          <span className="truncate">{trait.name}</span>
          <div className="flex flex-shrink-0 items-center gap-2">
            {appliedValuesCount ? (
              <span className="text-foreground/60">{appliedValuesCount}</span>
            ) : null}
            <ChevronIcon className="h-3 w-3 shrink-0 grow-0 basis-3 transition-transform group-data-[state=open]/collapsible-trait:rotate-180" />
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-3">
          {/* Search input for filtering trait values */}
          {trait.values.length > 5 && (
            <div
              className="relative flex h-9 w-full items-center gap-1 rounded-md border border-foreground/[0.1] bg-transparent px-2 transition-all duration-200 focus-within:ring-1 focus-within:ring-brand"
              onClick={(e) => e.stopPropagation()}
            >
              <SearchIcon className="h-6 w-6 shrink-0 text-foreground/40" />
              <Input
                placeholder={`Search ${trait.name.toLowerCase()}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border-none bg-transparent p-0 text-sm outline-none placeholder:text-foreground/40 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          )}

          {/* Filtered trait values */}
          {debouncedFilteredValues.length > 0
            ? debouncedFilteredValues.map(({ value, count }) => (
                <TraitValue
                  key={value}
                  traitName={trait.name}
                  traitValue={value}
                  count={count}
                />
              ))
            : searchQuery.trim() && (
                <div className="py-2 text-foreground/60 text-sm">
                  No matching traits found
                </div>
              )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}, "Trait")
